/**
 * Create Task access/assignment cross-validation.
 *
 * The Create Task has two independent gates:
 *   1. Access gate — can the actor open the URL (accessPolicy.mode + restrictions)?
 *   2. Assignment gate — does the actor's resolved identity match the assignee list
 *      so they get the `assignee` / `eligibleUser` role (can submit) vs falling
 *      back to `viewer` (cannot submit)?
 *
 * Getting either wrong silently breaks submissions for some or all of the people
 * the builder intended to reach. This module reports the misalignments before
 * they hit production.
 *
 * The same rules are mirrored by the Java validator at
 * services/integrations-service/.../forms/ReviewTaskAccessValidator.java so the
 * agent tool (`review_task`) refuses to persist broken configurations.
 *
 * Placeholder assignees (`${...}`) cannot be classified until runtime; they are
 * reported under `info` and never block save.
 */

export const ISSUE_LEVEL = Object.freeze({
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
});

export const ISSUE_CODE = Object.freeze({
  // Errors
  ACCESS_DISABLED: 'ACCESS_DISABLED',
  NO_ASSIGNEES: 'NO_ASSIGNEES',
  SELECTED_USERS_EMPTY: 'SELECTED_USERS_EMPTY',
  NO_ASSIGNEE_CAN_SUBMIT: 'NO_ASSIGNEE_CAN_SUBMIT',
  // Warnings
  PARTIAL_ASSIGNEE_LOCKOUT: 'PARTIAL_ASSIGNEE_LOCKOUT',
  EMAIL_MAGIC_LINK_IDENTITY_MISMATCH: 'EMAIL_MAGIC_LINK_IDENTITY_MISMATCH',
  AUTH_USERS_EMAIL_ASSIGNEE: 'AUTH_USERS_EMAIL_ASSIGNEE',
  ANONYMOUS_FLAG_INEFFECTIVE: 'ANONYMOUS_FLAG_INEFFECTIVE',
  // Info
  PLACEHOLDER_ASSIGNEES_UNVERIFIABLE: 'PLACEHOLDER_ASSIGNEES_UNVERIFIABLE',
});

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PLACEHOLDER_REGEX = /^\$\{.+\}$/;

function classifyAssignee(raw) {
  if (typeof raw !== 'string') return { kind: 'unknown', value: '' };
  const value = raw.trim();
  if (!value) return { kind: 'unknown', value };
  if (PLACEHOLDER_REGEX.test(value)) return { kind: 'placeholder', value };
  if (EMAIL_REGEX.test(value)) return { kind: 'email', value };
  return { kind: 'username', value };
}

function toLowerSet(list) {
  const set = new Set();
  for (const raw of Array.isArray(list) ? list : []) {
    if (typeof raw === 'string') {
      const trimmed = raw.trim().toLowerCase();
      if (trimmed) set.add(trimmed);
    }
  }
  return set;
}

/**
 * Decide whether an individual assignee (username | email | placeholder) can
 * pass the configured access gate. Returns 'yes' | 'no' | 'unknown'.
 */
function canAssigneePassAccessGate(classified, accessPolicy) {
  const mode = (accessPolicy?.mode || 'PUBLIC').toUpperCase();
  if (classified.kind === 'placeholder') return 'unknown';

  switch (mode) {
    case 'DISABLED':
      return 'no';

    case 'PUBLIC':
      // Anyone with the link passes. Any classified assignee can at least open it.
      return 'yes';

    case 'AUTHENTICATED_USERS':
      // Requires an account login. Email-only assignees have no account.
      return classified.kind === 'username' ? 'yes' : 'no';

    case 'AUTHENTICATED_SELECTED_USERS': {
      if (classified.kind !== 'username') return 'no';
      const selected = toLowerSet(accessPolicy?.selectedUserIds);
      return selected.has(classified.value.toLowerCase()) ? 'yes' : 'no';
    }

    case 'EMAIL_MAGIC_LINK': {
      if (classified.kind !== 'email') {
        // Usernames don't match the magic-link identity. They can technically
        // verify their own email if they know it, but at that point the
        // role-resolver won't match their username against the assignee list.
        return 'no';
      }
      const restriction = (accessPolicy?.emailRestriction || 'ANY').toUpperCase();
      if (restriction === 'ALLOWLIST') {
        const allowed = toLowerSet(accessPolicy?.allowedEmails);
        return allowed.has(classified.value.toLowerCase()) ? 'yes' : 'no';
      }
      if (restriction === 'DOMAIN') {
        const domains = toLowerSet(accessPolicy?.allowedDomains);
        const at = classified.value.indexOf('@');
        const domain = at >= 0 ? classified.value.slice(at + 1).toLowerCase() : '';
        return domains.has(domain) ? 'yes' : 'no';
      }
      return 'yes';
    }

    default:
      return 'unknown';
  }
}

function collectSpecificAssignees(responsibility) {
  const mode = responsibility?.activeMode;
  if (mode === 'ANYONE_ELIGIBLE') {
    const eligible = responsibility?.anyoneEligible || {};
    return Array.isArray(eligible.eligibleUserIds) ? eligible.eligibleUserIds : [];
  }
  if (mode === 'MANY_PEOPLE') {
    const many = responsibility?.manyPeople || {};
    return Array.isArray(many.assigneeUserIds) ? many.assigneeUserIds : [];
  }
  // ONE_PERSON or unknown: prefer the array, fall back to the legacy string.
  const one = responsibility?.onePerson || {};
  if (Array.isArray(one.assigneeUserIds) && one.assigneeUserIds.length > 0) {
    return one.assigneeUserIds;
  }
  return one.assigneeUserId ? [one.assigneeUserId] : [];
}

/**
 * Run every cross-field check. Returns a frozen array of issues:
 *   { level, code, message, offendingAssignees? }
 *
 * Callers decide how to present/enforce each issue:
 *   - ERROR issues should block save / return an `error` from the agent tool.
 *   - WARNING issues should be surfaced to the builder but not blocking.
 *   - INFO issues are purely advisory (e.g. unverifiable placeholders).
 */
export function validateReviewTaskAccess({ accessPolicy, responsibility } = {}) {
  const issues = [];
  const mode = (accessPolicy?.mode || 'PUBLIC').toUpperCase();
  const activeMode = (responsibility?.activeMode || 'ONE_PERSON').toUpperCase();
  const anyoneEligible = responsibility?.anyoneEligible || {};
  const allowAnonymous = Boolean(anyoneEligible.allowAnonymousWhenPublicLink);

  // ── 1. Access disabled with any assignment intent ─────────────────────────
  if (mode === 'DISABLED') {
    issues.push({
      level: ISSUE_LEVEL.ERROR,
      code: ISSUE_CODE.ACCESS_DISABLED,
      message:
        'Access is disabled, so nobody can open this Create Task. Change the access mode, or remove the Create Task.',
    });
  }

  // ── 2. AUTHENTICATED_SELECTED_USERS with empty allowlist ─────────────────
  if (mode === 'AUTHENTICATED_SELECTED_USERS') {
    const selected = toLowerSet(accessPolicy?.selectedUserIds);
    if (selected.size === 0) {
      issues.push({
        level: ISSUE_LEVEL.ERROR,
        code: ISSUE_CODE.SELECTED_USERS_EMPTY,
        message:
          '"Authenticated selected users" access requires at least one user in the access list.',
      });
    }
  }

  // ── 3. Assignment list empty for a mode that needs one ───────────────────
  const specificAssignees = collectSpecificAssignees(responsibility)
    .map((x) => (typeof x === 'string' ? x.trim() : ''))
    .filter(Boolean);

  if (activeMode === 'ONE_PERSON' || activeMode === 'MANY_PEOPLE') {
    if (specificAssignees.length === 0) {
      issues.push({
        level: ISSUE_LEVEL.ERROR,
        code: ISSUE_CODE.NO_ASSIGNEES,
        message: 'At least one assignee is required. Add a user, email, or placeholder.',
      });
    }
  } else if (activeMode === 'ANYONE_ELIGIBLE') {
    if (specificAssignees.length === 0 && !allowAnonymous) {
      issues.push({
        level: ISSUE_LEVEL.ERROR,
        code: ISSUE_CODE.NO_ASSIGNEES,
        message:
          '"Anyone eligible" needs either an eligible-user list or "Allow anonymous submissions via public link" enabled.',
      });
    }
  }

  // ── 4. Anonymous flag only works with PUBLIC access ──────────────────────
  if (allowAnonymous && mode !== 'PUBLIC' && mode !== 'DISABLED') {
    issues.push({
      level: ISSUE_LEVEL.WARNING,
      code: ISSUE_CODE.ANONYMOUS_FLAG_INEFFECTIVE,
      message:
        '"Allow anonymous submissions" only takes effect when the access mode is "Public". With the current access mode the anonymous flag is ignored.',
    });
  }

  // ── 5. EMAIL_MAGIC_LINK with username-style assignees ────────────────────
  // Magic-link users get actorId = verified email, but role resolution matches
  // against the assignee list. Username assignees will never match → they
  // always fall through to the viewer role and cannot submit.
  if (mode === 'EMAIL_MAGIC_LINK' && specificAssignees.length > 0) {
    const usernameAssignees = specificAssignees
      .map(classifyAssignee)
      .filter((c) => c.kind === 'username')
      .map((c) => c.value);
    if (usernameAssignees.length > 0) {
      issues.push({
        level: ISSUE_LEVEL.WARNING,
        code: ISSUE_CODE.EMAIL_MAGIC_LINK_IDENTITY_MISMATCH,
        message:
          'Email magic-link access identifies users by email, but these assignees are account usernames. They will pass the access gate only if they log in, not via the magic link — with a magic-link identity they land in the viewer role and cannot submit.',
        offendingAssignees: usernameAssignees,
      });
    }
  }

  // ── 6. AUTHENTICATED_USERS with raw-email assignees ──────────────────────
  // The access gate requires an account login; raw emails have no account.
  if (
    (mode === 'AUTHENTICATED_USERS' || mode === 'AUTHENTICATED_SELECTED_USERS') &&
    specificAssignees.length > 0
  ) {
    const emailAssignees = specificAssignees
      .map(classifyAssignee)
      .filter((c) => c.kind === 'email')
      .map((c) => c.value);
    if (emailAssignees.length > 0) {
      issues.push({
        level: ISSUE_LEVEL.WARNING,
        code: ISSUE_CODE.AUTH_USERS_EMAIL_ASSIGNEE,
        message:
          'This access mode requires a signed-in account, but these assignees are raw email addresses. They cannot log in and will be blocked at the access gate.',
        offendingAssignees: emailAssignees,
      });
    }
  }

  // ── 7. Per-assignee access-gate check ────────────────────────────────────
  // Evaluate each classifiable assignee. Count unknowns (placeholders) separately.
  let yesCount = 0;
  let unknownCount = 0;
  const lockedOut = [];
  const placeholders = [];
  for (const raw of specificAssignees) {
    const classified = classifyAssignee(raw);
    if (classified.kind === 'placeholder') {
      placeholders.push(classified.value);
      unknownCount += 1;
      continue;
    }
    const result = canAssigneePassAccessGate(classified, accessPolicy);
    if (result === 'yes') yesCount += 1;
    else if (result === 'unknown') unknownCount += 1;
    else lockedOut.push(classified.value);
  }

  if (specificAssignees.length > 0 && lockedOut.length > 0) {
    const anyoneCanSubmitAnonymously = mode === 'PUBLIC' && allowAnonymous;
    if (yesCount === 0 && unknownCount === 0 && !anyoneCanSubmitAnonymously) {
      // None of the listed assignees (and no placeholders) can pass the gate.
      // Only skip this as an error if it's already covered by ACCESS_DISABLED
      // or SELECTED_USERS_EMPTY — those are the root cause; this is the symptom.
      const alreadyFatal = issues.some(
        (i) =>
          i.level === ISSUE_LEVEL.ERROR &&
          (i.code === ISSUE_CODE.ACCESS_DISABLED || i.code === ISSUE_CODE.SELECTED_USERS_EMPTY)
      );
      if (!alreadyFatal) {
        issues.push({
          level: ISSUE_LEVEL.ERROR,
          code: ISSUE_CODE.NO_ASSIGNEE_CAN_SUBMIT,
          message:
            'No assignee can pass the current access gate, so the task cannot be submitted by anyone.',
          offendingAssignees: lockedOut,
        });
      }
    } else if (lockedOut.length > 0) {
      issues.push({
        level: ISSUE_LEVEL.WARNING,
        code: ISSUE_CODE.PARTIAL_ASSIGNEE_LOCKOUT,
        message:
          'Some assignees cannot pass the access gate and will not be able to submit.',
        offendingAssignees: lockedOut,
      });
    }
  }

  // ── 8. Placeholder assignees are unverifiable (info only) ────────────────
  if (placeholders.length > 0) {
    issues.push({
      level: ISSUE_LEVEL.INFO,
      code: ISSUE_CODE.PLACEHOLDER_ASSIGNEES_UNVERIFIABLE,
      message:
        'Some assignees are workflow placeholders and can only be validated at runtime. Make sure whatever they resolve to can pass the configured access gate.',
      offendingAssignees: placeholders,
    });
  }

  return issues;
}

export function hasBlockingIssues(issues) {
  return Array.isArray(issues) && issues.some((i) => i && i.level === ISSUE_LEVEL.ERROR);
}

/** Issues that mean a typed assignee cannot open or submit under the current access mode. */
export function isSendTaskAssigneeAccessIssue(issue) {
  if (!issue) return false;
  return [
    ISSUE_CODE.NO_ASSIGNEE_CAN_SUBMIT,
    ISSUE_CODE.PARTIAL_ASSIGNEE_LOCKOUT,
    ISSUE_CODE.EMAIL_MAGIC_LINK_IDENTITY_MISMATCH,
    ISSUE_CODE.AUTH_USERS_EMAIL_ASSIGNEE,
  ].includes(issue.code);
}

/**
 * Suggest one-click access-policy changes that unblock the current assignee list.
 * Each fix: { id, label, nextAccessPolicy }.
 */
export function buildReviewTaskAccessQuickFixes({ accessPolicy, issues } = {}) {
  const policy = accessPolicy || { mode: 'PUBLIC', selectedUserIds: [] };
  const mode = (policy.mode || 'PUBLIC').toUpperCase();
  const fixes = [];
  const seen = new Set();

  const pushFix = (id, label, nextAccessPolicy) => {
    if (seen.has(id)) return;
    seen.add(id);
    fixes.push({ id, label, nextAccessPolicy });
  };

  const actionable = (Array.isArray(issues) ? issues : []).filter(isSendTaskAssigneeAccessIssue);
  if (actionable.length === 0) return fixes;

  const lockedOut = new Set();
  for (const issue of actionable) {
    for (const raw of issue.offendingAssignees || []) {
      if (raw) lockedOut.add(String(raw).trim());
    }
  }
  const lockedList = [...lockedOut];
  const lockedClassified = lockedList.map(classifyAssignee);
  const emailAssignees = lockedClassified.filter((c) => c.kind === 'email').map((c) => c.value);
  const usernameAssignees = lockedClassified.filter((c) => c.kind === 'username').map((c) => c.value);

  if (
    actionable.some((i) => i.code === ISSUE_CODE.AUTH_USERS_EMAIL_ASSIGNEE) ||
    (emailAssignees.length > 0 &&
      (mode === 'AUTHENTICATED_USERS' || mode === 'AUTHENTICATED_SELECTED_USERS'))
  ) {
    pushFix('email_magic_link', 'Switch access to Email magic link', {
      ...policy,
      mode: 'EMAIL_MAGIC_LINK',
      emailRestriction: 'ANY',
      selectedUserIds: [],
      allowedEmails: [],
      allowedDomains: [],
    });
  }

  if (
    actionable.some((i) => i.code === ISSUE_CODE.EMAIL_MAGIC_LINK_IDENTITY_MISMATCH) ||
    (usernameAssignees.length > 0 && mode === 'EMAIL_MAGIC_LINK')
  ) {
    pushFix('authenticated_users', 'Switch access to Authenticated users', {
      ...policy,
      mode: 'AUTHENTICATED_USERS',
      selectedUserIds: [],
      emailRestriction: 'ANY',
      allowedEmails: [],
      allowedDomains: [],
    });
  }

  if (
    mode === 'AUTHENTICATED_SELECTED_USERS' &&
    usernameAssignees.length > 0
  ) {
    const existing = Array.isArray(policy.selectedUserIds) ? policy.selectedUserIds : [];
    const nextIds = Array.from(new Set([...existing, ...usernameAssignees]));
    pushFix('add_selected_users', 'Add assignees to the access allowlist', {
      ...policy,
      selectedUserIds: nextIds,
    });
  }

  const emailRestriction = (policy.emailRestriction || 'ANY').toUpperCase();
  if (mode === 'EMAIL_MAGIC_LINK' && emailAssignees.length > 0) {
    if (emailRestriction === 'ALLOWLIST') {
      const existing = Array.isArray(policy.allowedEmails) ? policy.allowedEmails : [];
      pushFix('allowlist_emails', 'Add assignee emails to the access allowlist', {
        ...policy,
        allowedEmails: Array.from(new Set([...existing, ...emailAssignees])),
      });
    }
    if (emailRestriction === 'DOMAIN') {
      const domains = emailAssignees
        .map((email) => {
          const at = email.indexOf('@');
          return at >= 0 ? email.slice(at + 1).toLowerCase() : '';
        })
        .filter(Boolean);
      const existing = Array.isArray(policy.allowedDomains) ? policy.allowedDomains : [];
      pushFix('allow_domains', 'Allow assignee email domains', {
        ...policy,
        allowedDomains: Array.from(new Set([...existing, ...domains])),
      });
      pushFix('any_email', 'Allow any email domain', {
        ...policy,
        emailRestriction: 'ANY',
        allowedDomains: [],
      });
    }
  }

  if (emailAssignees.length > 0 && usernameAssignees.length > 0 && mode !== 'PUBLIC') {
    pushFix('public_access', 'Switch access to Public (anyone with the link)', {
      ...policy,
      mode: 'PUBLIC',
      selectedUserIds: [],
      emailRestriction: 'ANY',
      allowedEmails: [],
      allowedDomains: [],
    });
  }

  return fixes;
}
